/** Tabela Preço D0/D-1/Δ/MWm/MWh + BarChart MWh com linha Preço D0.
 * #228 item 5: coluna PnL REMOVIDA — Curta e Longa padronizadas com as
 * mesmas colunas (o FV agregado vive na ResultadoTable). `pnl` segue no
 * payload do backend e no tipo (ignorado aqui). */
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { numBr } from "../../format";

export interface CurvaRow {
  vertice: string;
  precoD0: number | null;
  precoD1: number | null; // #55: null até existir snapshot D-1 → "—"
  delta: number | null;
  // GO 03/08 + #61: mês de granularidade anual agora vem PREENCHIDO pelo mês
  // de entrega dos trades (posição também contida no vértice anual da Longa —
  // a nota declara). null = gap anômalo do feed no ano corrente → "—".
  mwm: number | null;
  mwh: number | null;
  pnl?: number | null; // presente no payload; não exibido (#228 item 5)
  cobertura?: "mensal" | "anual";
  // #61: volume em produto MULTI-MÊS do ano, que fica SÓ no vértice anual
  // (não fatiado por mês) — declarado na linha quando existir.
  mwh_produto_anual?: number | null;
}

interface Props {
  titulo: string;
  rows: CurvaRow[];
  totalMwh?: number;
  /** GO 03/08: nota de cobertura do backend (ex.: "granularidade anual a
   * partir de 2027 — ..."); exibida como rodapé da tabela quando presente. */
  nota?: string | null;
}

/** #55: numérico ou "—" quando null (D-1/Δ aguardando 2º snapshot). */
function nOrDash(v: number | null | undefined, dec?: number): string {
  return v === null || v === undefined ? "—" : numBr(v, dec);
}

export function CurvaTable({ titulo, rows, totalMwh, nota }: Props) {
  const chartData = rows.map((r) => ({
    vertice: r.vertice,
    mwh: r.mwh ?? 0, // fora da cobertura: sem barra (a tabela mostra "—")
    precoD0: r.precoD0,
  }));

  return (
    <div className="bg-indra-azul-surface rounded-lg border border-white/10 p-4">
      <h3 className="text-white font-semibold text-sm mb-4">{titulo}</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">
                  Vértice
                </th>
                <th className="text-right py-2 px-3 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">
                  D0
                </th>
                <th className="text-right py-2 px-3 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">
                  D-1
                </th>
                <th className="text-right py-2 px-3 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">
                  Δ
                </th>
                <th className="text-right py-2 px-3 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">
                  MWm
                </th>
                <th className="text-right py-2 px-3 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">
                  MWh
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                  <td className="py-2 px-3 text-sm text-white">{r.vertice}</td>
                  <td className="py-2 px-3 text-right text-sm font-mono tabular-nums text-white">
                    {nOrDash(r.precoD0)}
                  </td>
                  <td className="py-2 px-3 text-right text-sm font-mono tabular-nums text-white/70">
                    {nOrDash(r.precoD1)}
                  </td>
                  <td
                    className={
                      "py-2 px-3 text-right text-sm font-mono tabular-nums " +
                      ((r.delta ?? 0) > 0 ? "text-indra-turquesa" : (r.delta ?? 0) < 0 ? "text-red-400" : "text-white/40")
                    }
                  >
                    {nOrDash(r.delta)}
                  </td>
                  <td
                    className={
                      "py-2 px-3 text-right text-sm font-mono tabular-nums " +
                      ((r.mwm ?? 0) < 0 ? "text-red-400" : r.mwm === null ? "text-white/40" : "text-white")
                    }
                  >
                    {nOrDash(r.mwm)}
                  </td>
                  <td
                    className={
                      "py-2 px-3 text-right text-sm font-mono tabular-nums " +
                      ((r.mwh ?? 0) < 0 ? "text-red-400" : r.mwh === null ? "text-white/40" : "text-white")
                    }
                  >
                    {nOrDash(r.mwh, 0)}
                    {r.mwh_produto_anual != null && (
                      <div className="text-[10px] leading-tight text-white/40">
                        {r.mwh_produto_anual > 0 ? "+" : ""}
                        {numBr(r.mwh_produto_anual, 0)} MWh em produto anual (vértice do ano)
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {totalMwh !== undefined && (
                <tr className="bg-indra-azul-deep font-semibold">
                  <td className="py-2 px-3 text-sm text-indra-turquesa uppercase tracking-wide">
                    Total
                  </td>
                  <td />
                  <td />
                  <td />
                  <td />
                  <td className="py-2 px-3 text-right text-sm font-mono tabular-nums text-white">
                    {numBr(totalMwh, 0)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {nota && (
            <p className="mt-2 text-xs text-white/50 italic">{nota}</p>
          )}
        </div>

        <div className="h-[260px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
              <XAxis
                dataKey="vertice"
                stroke="rgba(255,255,255,0.5)"
                fontSize={10}
                tickLine={false}
                axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
              />
              <YAxis
                yAxisId="mwh"
                stroke="rgba(255,255,255,0.5)"
                fontSize={10}
                tickLine={false}
                axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                tickFormatter={(v) => numBr(v, 0)}
              />
              <YAxis
                yAxisId="preco"
                orientation="right"
                stroke="rgba(0,218,200,0.6)"
                fontSize={10}
                tickLine={false}
                axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                tickFormatter={(v) => numBr(v, 0)}
              />
              <Tooltip
                contentStyle={{
                  background: "#001528",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                labelStyle={{ color: "rgba(255,255,255,0.8)" }}
                formatter={(v: number, name: string) =>
                  name === "precoD0" ? numBr(v) : numBr(v, 0)
                }
              />
              <Bar yAxisId="mwh" dataKey="mwh" name="MWh" radius={[3, 3, 0, 0]}>
                {chartData.map((p, i) => (
                  <Cell key={`c-${i}`} fill={p.mwh < 0 ? "#FF6B6B" : "#4FB3E8"} />
                ))}
              </Bar>
              <Line
                yAxisId="preco"
                type="monotone"
                dataKey="precoD0"
                name="Preço D0"
                stroke="#00DAC8"
                strokeWidth={2}
                dot={{ r: 3, fill: "#00DAC8" }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
